import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Header from "../components/Header";
import api from "../lib/api";
import { useCurrency } from "../lib/currency";
import { useI18n } from "../lib/i18n";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import CatalogImage from "../components/CatalogImage";
import ProductGallery from "../components/ProductGallery";
import CheckoutDialog from "../components/CheckoutDialog";
import QuantityStepper from "../components/QuantityStepper";
import OrderTotals from "../components/OrderTotals";

export default function ProductDetail() {
  const { id } = useParams();
  const { t } = useI18n();
  const { money, altMoney, fx, refreshFx } = useCurrency();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [p, setP] = useState(null);
  const [qty, setQty] = useState(1);
  const [pms, setPms] = useState([]);
  const [paymentMethod, setPaymentMethod] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [orderMin, setOrderMin] = useState(0);

  useEffect(() => {
    api.get("/settings/order-min").then((r) => setOrderMin(r.data.min_idr || 0)).catch(() => {});
  }, []);

  useEffect(() => {
    api.get(`/products/${id}`).then(r => setP(r.data)).catch(() => setP(false));
    api.get("/payment-methods").then(r => { setPms(r.data); setPaymentMethod((cur) => (cur && r.data.some((m) => m.name === cur) ? cur : r.data[0]?.name || "")); });
  }, [id]);

  const openCheckout = () => {
    if (!user) { toast.error(t("product.login_first")); navigate("/login"); return; }
    if (user.role !== "buyer") { toast.error(t("product.buyers_only")); return; }
    if (!(qty >= 1 && qty <= p.stock_available)) { toast.error(t("product.invalid_qty")); return; }
    const minNeeded = Math.max(orderMin, p.min_order_idr || 0);
    if (minNeeded > 0 && p.sell_price_idr * qty < minNeeded) { toast.error(t("product.min_order_err").replace("{amount}", money(minNeeded))); return; }
    setCheckoutOpen(true);
  };

  const selectedPm = pms.find((m) => m.name === paymentMethod);
  const buy = async (delivery, useBalance) => {
    setSubmitting(true);
    try {
      const r = await api.post("/orders", {
        items: [{ product_id: p.id, quantity: qty }],
        payment_method: paymentMethod, delivery, use_balance: useBalance,
        ...(selectedPm?.asset === "USDT" && fx ? { quoted_fx_rate: fx.rate } : {}),
      });
      toast.success(`${t("product.order_created")}: ${r.data.order_no}`);
      navigate(`/orders/${r.data.id}`);
    } catch (err) {
      if (err?.response?.status === 409 && err.response.data?.detail?.code === "fx_changed") { await refreshFx(); toast.error(t("checkout.fx_changed")); return; }
      toast.error(err?.response?.data?.detail || t("product.order_failed"));
    } finally { setSubmitting(false); }
  };

  if (p === false) return (
    <div className="min-h-screen bg-[#101114]"><Header />
      <div data-testid="product-not-found" className="max-w-xl mx-auto px-6 py-24 text-center">
        <h1 className="text-2xl font-bold text-[#F5F3ED] mb-3" style={{fontFamily:"'Playfair Display', serif"}}>{t("product.not_found")}</h1>
        <p className="text-sm text-[#A8A8AD] mb-8">{t("product.not_found_sub")}</p>
        <button data-testid="product-not-found-back-btn" onClick={() => navigate("/marketplace")} className="bg-[#D4AF37] text-[#101114] hover:bg-[#F2D98D] font-bold rounded-sm px-6 py-2.5 text-sm">{t("product.back")}</button>
      </div>
    </div>
  );
  if (!p) return <div className="min-h-screen bg-[#101114]"><Header /><div className="p-10 text-[#A8A8AD]">{t("common.loading")}</div></div>;

  return (
    <div className="min-h-screen bg-[#101114]">
      <Header />
      <div className="max-w-5xl mx-auto px-6 lg:px-12 py-6 lg:py-10 grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-10 lg:items-stretch">
        <ProductGallery key={p.id} product={p} />
        <div data-testid="product-info-col" className="flex flex-col min-w-0">
          <div>
          <div className="text-xs font-bold uppercase tracking-[0.3em] text-[#D4AF37] mb-2">{p.category_slug}</div>
          {p.server_name && (
            <div data-testid="product-server-info" className="text-xs text-[#A8A8AD] mb-3">
              Server: {p.server_name}{p.region ? ` · ${p.region}` : ""}{p.faction ? ` · ${p.faction}` : ""}
            </div>
          )}
          <h1 className="text-2xl lg:text-3xl font-bold break-words text-[#F5F3ED] mb-4" style={{fontFamily:"'Playfair Display', serif"}}>{p.name}</h1>
          <p className="text-sm text-[#A8A8AD] mb-6 leading-relaxed">{p.description}</p>
          <div data-testid="product-price" className="text-3xl font-bold text-[#D4AF37]">{money(p.sell_price_idr)}</div>
          <div data-testid="product-alt-price" className="text-xs text-[#A8A8AD] mb-2">{altMoney(p.sell_price_idr)} / {p.unit}</div>
          <div className="text-xs text-[#A8A8AD] mb-6">{t("market.stock")}: {p.stock_available} × {p.unit}</div>
          {Math.max(orderMin, p.min_order_idr || 0) > 0 && <div data-testid="product-min-order" className="-mt-4 mb-6 text-xs text-[#F2D98D]">{t("product.min_order")}: {money(Math.max(orderMin, p.min_order_idr || 0))}</div>}
          </div>

          <div data-testid="product-order-form" className="mt-auto bg-[#202127] border border-[#35353B] rounded-lg p-4 space-y-3">
            <QuantityStepper qty={qty} setQty={setQty} stock={p.stock_available} unit={p.unit} />
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.2em] text-[#D4AF37]">{t("checkout.payment_method")}</label>
              <select data-testid="product-payment-select" value={paymentMethod} onChange={(e)=>setPaymentMethod(e.target.value)}
                className="mt-1.5 bg-[#191A1F] border border-[#35353B] text-[#F5F3ED] rounded-sm px-3 h-10 w-full outline-none truncate">
                {pms.map(m => <option key={m.id} value={m.name}>{m.name} ({m.asset === "USDT" ? `USDT${m.network ? ` ${m.network}` : ""}` : m.currency || "IDR"}){m.fee_percent ? ` +${m.fee_percent}%` : ""}</option>)}
              </select>
            </div>
            <OrderTotals price={p.sell_price_idr} qty={qty} pm={pms.find((m) => m.name === paymentMethod)} />
            <button data-testid="product-buy-btn" onClick={openCheckout} disabled={submitting || p.stock_available < 1}
              className="w-full bg-[#D4AF37] text-[#101114] hover:bg-[#F2D98D] disabled:opacity-50 transition-colors font-bold rounded-sm py-2.5">
              {submitting ? t("common.loading") : t("product.add_to_order")}
            </button>
          </div>
        </div>
      </div>
      <CheckoutDialog key={checkoutOpen ? "open" : "closed"} open={checkoutOpen} onOpenChange={setCheckoutOpen} product={p} qty={qty}
        paymentMethod={pms.find((m) => m.name === paymentMethod)} onConfirm={buy} submitting={submitting} />
    </div>
  );
}
